import type { SurfaceField } from './modSurface';

/**
 * capeShape.ts (M153) — where the Fan Tifo cape's cloth lies at rest and where
 * it flies at speed, worked out from the ride's MEASURED shape (modSurface).
 *
 * The cloth hangs from a rail at `anchor` and runs BACK (-z). At rest each
 * column of the cloth follows the TAUT profile of the body under it — the
 * upper convex hull of the body's top (from above) and its tail (from
 * behind), lifted by a stand-off — so it spans a gap (the deck under a GT
 * wing, the engine cover beside a shark fin) instead of tearing into it,
 * sags a little across long spans, rolls over the tail's rearmost point (a
 * step bumper, a tow hitch, a diffuser) and then falls straight down. Across
 * the car the cloth is a sheet: a column can fall away from its neighbour no
 * faster than `crossSlope`, so a fin or a narrow tail makes a gentle tent,
 * never a cut. Flying, it streams straight back from the rail but never dips
 * below the body (the Formula's rear wing stands taller than its airbox).
 *
 * M153 (owner, on the rebuilt fleet): the M128c cape lay ON the per-vertex
 * surface, so the sports car's louvres, the coupe's wing, the pickup's faceted
 * tailgate, the monster's tail panel and the Formula's fin all came THROUGH
 * the cloth, and its hem tucked forward into the bumpers.
 */

export interface CapeSpec {
  /** The rail's centre (car-local). The cloth starts here. */
  anchor: { x: number; y: number; z: number };
  /** Cloth width (across the car). */
  W: number;
  /** The longest the cloth may be (it is cut to end where its hang meets the
   *  floor when the body is shorter). */
  Lmax: number;
  /** Stand-off between the cloth and the body. */
  clear: number;
  /** The lowest the hanging cloth reaches (car-local y). */
  hangFloor: number;
  /** Half the rail's width: the cloth gathers to it at the top edge. */
  railHalf: number;
  /** How fast a column may fall away from its neighbour (dy per unit x). */
  crossSlope?: number;
  /** Extra clearance kept over the body while flying (the flutter). */
  flyClear?: number;
  /** Sag at full flight at the free end (fraction of the length). */
  flyDroop?: number;
  /** M154: boxes in car-local (z, y), spanning the cloth's width, that stand
   *  between the race camera and the cloth (another mod over it): the design
   *  is laid in the longest stretch of cloth they do not hide. */
  covers?: readonly { z0: number; z1: number; y0: number; y1: number }[];
  /** M154 polish r4: the cloth width the design is sized for, when the cloth
   *  itself was narrowed below it (the bike's, to its tail): the design keeps
   *  the size it had on the wider cloth (modKit.capeDesignBox's `wide`). */
  designW?: number;
}

export interface CapeShape {
  /** The cloth's length (≤ Lmax). */
  L: number;
  /** Rest offset per cloth vertex, in the cloth frame (x back along the
   *  cloth, y across toward +X, z down). u = 0..1 from the rail, v = 0..1
   *  across (v = 0 on the +X side). */
  drape: (u: number, v: number) => [number, number, number];
  /** Flight offset per cloth vertex, same frame. */
  fly: (u: number, v: number) => [number, number, number];
  /** Cloth height over a car-local (x, z) at rest / flying; null off the
   *  part of the cloth that lies over the body. */
  restAt: (x: number, z: number) => number | null;
  flyAt: (x: number, z: number) => number | null;
  /** Where the hang starts (fraction of L) on the centre column — the fold
   *  the design is placed around. */
  foldU: number;
  /** M154: the part of the cloth (fractions of L from the rail) the design
   *  is laid in: the whole cloth, or the longest stretch the race camera
   *  sees past the spec's covers. */
  band: [number, number];
  /** How LONG the middle of the cloth looks from the race camera, per unit
   *  of cloth (1 = seen square on; a sheet lying flat is foreshortened to
   *  about sin of the camera's elevation, a sheet hanging down the tail to
   *  about its cosine). The design is drawn taller on a cloth that mostly
   *  LIES (the Formula's, the bike's) so it reads round from the chase
   *  camera instead of squashed flat. */
  chaseView: number;
}

/** The race camera's elevation over the car's tail (tuning.camera: 4.4 up,
 *  10 back, looking at a point a little ahead — about 24 degrees). */
export const CHASE_ELEVATION = 0.42;

/** M154 polish r2: the resting cloth's soft folds — their height at the hem
 *  and how far apart they run across it. */
export const CAPE_FOLD = 0.028;
export const CAPE_FOLD_PITCH = 0.32;

/** M154 polish r4: a cloth whose hang below its turn over the tail's edge is
 *  shorter than HEM_TURN fades its folds out over its last HEM_SMOOTH. */
export const HEM_TURN = 0.15;
export const HEM_SMOOTH = 0.12;

/** The fold height at cloth point (u along from the rail, v across) on a
 *  cloth `W` wide (pure; the tests pin it): never negative (lifted off the
 *  body, never into it), nothing at the rail, CAPE_FOLD at the hem's ridges;
 *  the ridges wander a little across so they read as cloth, not a washboard. */
export function capeFold(u: number, v: number, W: number): number {
  const n = Math.max(2, Math.round(W / CAPE_FOLD_PITCH));
  const ph = 2 * Math.PI * (n * v + 0.18 * Math.sin(3.1 * u + 1.7 * v * n));
  return CAPE_FOLD * Math.pow(Math.max(0, Math.min(1, u)), 1.3) * 0.5 * (1 - Math.cos(ph));
}

interface Path {
  /** Back distance from the anchor (anchor.z - z) per point. */
  s: number[];
  y: number[];
  /** Arc length to each point. */
  len: number[];
}

const COLS = 24;
const DS = 0.025;

/** The upper hull of (s, y) points sorted by s, as indices. */
function upperHull(ps: { s: number; y: number }[]): { s: number; y: number }[] {
  const out: { s: number; y: number }[] = [];
  for (const p of ps) {
    while (out.length >= 2) {
      const o = out[out.length - 2]!;
      const a = out[out.length - 1]!;
      const cross = (a.s - o.s) * (p.y - o.y) - (a.y - o.y) * (p.s - o.s);
      if (cross >= 0) out.pop();
      else break;
    }
    out.push(p);
  }
  return out;
}

/** True when the race camera's line of sight from cloth point (z, y) — up
 *  and back at CHASE_ELEVATION — passes through the box. */
export function hiddenBy(z: number, y: number, b: { z0: number; z1: number; y0: number; y1: number }): boolean {
  if (z >= b.z0 && z <= b.z1 && y >= b.y0 && y <= b.y1) return true;
  if (z < b.z0) return false; // the box stands forward of the point
  const c = Math.cos(CHASE_ELEVATION);
  const s = Math.sin(CHASE_ELEVATION);
  // the ray (z - t c, y + t s), t >= 0, crosses z in [z0, z1] for t in:
  const t0 = Math.max(0, (z - b.z1) / c);
  const t1 = (z - b.z0) / c;
  const ya = y + t0 * s;
  const yb = y + t1 * s;
  return yb >= b.y0 && ya <= b.y1;
}

/** M154: the band of the cloth the design is laid in: the whole cloth, or
 *  the longest stretch of the centre column no cover hides from the race
 *  camera (at least a fifth of the cloth). */
function designBand(spec: CapeSpec, centre: Path, L: number): [number, number] {
  const covers = spec.covers ?? [];
  if (!covers.length) return [0, 1];
  const N = 100;
  const seen: boolean[] = [];
  for (let i = 0; i <= N; i++) {
    const d = (i / N) * L;
    let k = 1;
    while (k < centre.len.length - 1 && centre.len[k]! < d) k++;
    const seg = centre.len[k]! - centre.len[k - 1]!;
    const f = seg > 1e-9 ? Math.min(1, Math.max(0, (d - centre.len[k - 1]!) / seg)) : 1;
    const sb = centre.s[k - 1]! + (centre.s[k]! - centre.s[k - 1]!) * f;
    const yb = centre.y[k - 1]! + (centre.y[k]! - centre.y[k - 1]!) * f;
    const z = spec.anchor.z - sb;
    seen.push(!covers.some((b) => hiddenBy(z, yb, b)));
  }
  let best: [number, number] = [0, 1];
  let bestLen = -1;
  for (let i = 0; i <= N; ) {
    if (!seen[i]) {
      i++;
      continue;
    }
    let j = i;
    while (j < N && seen[j + 1]) j++;
    if (j - i > bestLen) {
      bestLen = j - i;
      best = [i / N, j / N];
    }
    i = j + 1;
  }
  if (bestLen < 0) return [0, 1];
  // never thinner than a fifth of the cloth: grow it toward the free end
  if (best[1] - best[0] < 0.2) best = [Math.max(0, Math.min(best[0], 0.8)), Math.max(best[1], Math.min(1, best[0] + 0.2))];
  return best;
}

export function capeShape(surf: SurfaceField | null, spec: CapeSpec, tableTop: (x: number, z: number) => number | null): CapeShape {
  const { anchor, W, clear, hangFloor } = spec;
  const k = spec.crossSlope ?? 0.9;
  const reachMax = spec.Lmax + 0.3;
  // The envelope the cloth spans: the top raised to the largest value within
  // about one cloth cell, so no ridge between two vertices can poke through.
  const env = surf ? surf.envelope(Math.max(0.04, W / 14), 0.05) : null;
  const topAt = (x: number, z: number): number | null => (env ? env.topAt(x, z) : tableTop(x, z));
  const nz = Math.max(2, Math.ceil(reachMax / DS));
  const xs: number[] = [];
  for (let j = 0; j <= COLS; j++) xs.push(anchor.x + (0.5 - j / COLS) * W);
  // Column top profiles over the body, then the cross-slope sheet rule.
  const T: (number | null)[][] = xs.map((x) => {
    const col: (number | null)[] = [];
    for (let i = 0; i <= nz; i++) col.push(topAt(x, anchor.z - i * DS));
    return col;
  });
  const C: (number | null)[][] = xs.map((_, j) =>
    T[j]!.map((_v, i) => {
      let best: number | null = T[j]![i] ?? null;
      for (let jj = 0; jj <= COLS; jj++) {
        const t = T[jj]![i];
        if (t === null || t === undefined) continue;
        const cand = t - k * Math.abs(xs[jj]! - xs[j]!);
        if (best === null || cand > best) best = cand;
      }
      return best;
    }),
  );
  // The tail seen from behind across the cloth's band: the rearmost point per
  // height (one profile for the whole width, so the hang is a straight sheet).
  const bx0 = anchor.x - W / 2 - 0.04;
  const bx1 = anchor.x + W / 2 + 0.04;
  const rear: { s: number; y: number }[] = [];
  if (surf) {
    for (let y = anchor.y; y >= hangFloor - 1e-6; y -= 0.03) {
      const rz = surf.rearZ(bx0, bx1, y - 0.015, y + 0.015);
      if (rz === null) continue;
      const s = anchor.z - rz;
      if (s > 0.02 && s < reachMax) rear.push({ s: s + clear, y });
    }
  }
  const paths: Path[] = xs.map((_, j) => {
    const pts: { s: number; y: number }[] = [{ s: 0, y: anchor.y }];
    const base: (number | null)[] = [];
    for (let i = 1; i <= nz; i++) {
      const t = C[j]![i];
      base[i] = t === null || t === undefined ? null : t + clear;
      if (base[i] !== null) pts.push({ s: i * DS, y: base[i]! });
    }
    for (const r of rear) pts.push(r);
    pts.sort((p, q) => p.s - q.s || q.y - p.y);
    const hull = upperHull(pts);
    // Resample the hull every DS, sag long gentle spans (never below the
    // body), then fall straight down to the floor.
    const s: number[] = [];
    const y: number[] = [];
    for (let h = 0; h + 1 < hull.length; h++) {
      const p = hull[h]!;
      const q = hull[h + 1]!;
      const span = q.s - p.s;
      const steps = Math.max(1, Math.ceil(span / DS));
      const seg = Math.hypot(span, q.y - p.y);
      const gentle = span > 1e-3 && Math.abs((q.y - p.y) / span) < 1.2;
      const sag = gentle ? Math.min(0.1, 0.05 * seg) : 0;
      for (let t = 0; t < steps; t++) {
        const f = t / steps;
        const ss = p.s + span * f;
        let yy = p.y + (q.y - p.y) * f - sag * 4 * f * (1 - f);
        const bi = Math.round(ss / DS);
        const b = bi >= 1 && bi <= nz ? base[bi] : null;
        if (b !== null && b !== undefined && yy < b) yy = b;
        s.push(ss);
        y.push(yy);
      }
    }
    const last = hull[hull.length - 1]!;
    s.push(last.s);
    y.push(last.y);
    if (last.y > hangFloor) {
      s.push(last.s);
      y.push(hangFloor);
    }
    const len: number[] = [0];
    for (let i = 1; i < s.length; i++) len.push(len[i - 1]! + Math.hypot(s[i]! - s[i - 1]!, y[i]! - y[i - 1]!));
    return { s, y, len };
  });
  let L = spec.Lmax;
  for (const p of paths) L = Math.min(L, p.len[p.len.length - 1]!);
  L = Math.max(0.2, L);

  const col = (v: number): Path => paths[Math.max(0, Math.min(COLS, Math.round(v * COLS)))]!;
  const along = (p: Path, d: number): [number, number] => {
    const n = p.len.length;
    if (d <= 0) return [p.s[0]!, p.y[0]!];
    for (let i = 1; i < n; i++) {
      if (p.len[i]! >= d) {
        const seg = p.len[i]! - p.len[i - 1]!;
        const f = seg > 1e-9 ? (d - p.len[i - 1]!) / seg : 1;
        return [p.s[i - 1]! + (p.s[i]! - p.s[i - 1]!) * f, p.y[i - 1]! + (p.y[i]! - p.y[i - 1]!) * f];
      }
    }
    return [p.s[n - 1]!, p.y[n - 1]!];
  };
  // The top edge gathers to the rail and opens to the full width.
  const ug = Math.min(0.3, 0.35 / L);
  const gather = (u: number, v: number): number => {
    const t = Math.min(1, u / ug);
    const e = t * t * (3 - 2 * t);
    const Wg = spec.railHalf * 2 + (W - spec.railHalf * 2) * e;
    return (0.5 - v) * (Wg - W);
  };
  const flyClear = spec.flyClear ?? 0.1;
  const droopK = spec.flyDroop ?? 0.16;
  const flyY = (j: number, s: number): number => {
    const u = Math.min(1, s / L);
    let y = anchor.y - droopK * L * Math.pow(u, 1.6);
    const i = Math.round(s / DS);
    const t = i >= 1 && i <= nz ? C[j]![i] : null;
    if (t !== null && t !== undefined) y = Math.max(y, t + clear + flyClear);
    return y;
  };
  // Where the centre column starts to fall (the last hull point it reaches
  // before the drop) — the fold the design is laid out around.
  const centre = col(0.5);
  let foldLen = centre.len[centre.len.length - 1]!;
  for (let i = 1; i < centre.s.length; i++) {
    if (centre.y[i]! < centre.y[i - 1]! - 0.6 * (centre.len[i]! - centre.len[i - 1]!)) {
      foldLen = centre.len[i - 1]!;
      break;
    }
  }
  const foldU = Math.min(1, foldLen / L);
  // M154 polish r4: a cloth that turns over the tail's edge within its last
  // HEM_TURN keeps its hem straight: the folds fade out over the last
  // HEM_SMOOTH of it. Rising off the lying deck and back off the hanging
  // stretch, the ridges bent the hem's white border into a torn zigzag right
  // at the edge (the muscle car's, from the race camera).
  const shortHang = foldU < 1 && (1 - foldU) * L < HEM_TURN;
  const hemTaper = (u: number): number => {
    if (!shortHang) return 1;
    const t = Math.max(0, Math.min(1, ((1 - u) * L) / HEM_SMOOTH));
    return t * t * (3 - 2 * t);
  };
  const band = designBand(spec, centre, L);
  // The middle half of the band on the centre column, as the race camera
  // sees it: each stretch of cloth heading back at (ds, dy) shows
  // |cos e * dy - sin e * ds| of its length (lying flat: sin e; hanging
  // straight down: cos e).
  const se = Math.sin(CHASE_ELEVATION);
  const ce = Math.cos(CHASE_ELEVATION);
  let seen = 0;
  let span = 0;
  const m0 = (band[0] + 0.25 * (band[1] - band[0])) * L;
  const m1 = (band[0] + 0.75 * (band[1] - band[0])) * L;
  for (let i = 1; i < centre.s.length; i++) {
    const a = Math.max(centre.len[i - 1]!, m0);
    const b = Math.min(centre.len[i]!, m1);
    const seg = centre.len[i]! - centre.len[i - 1]!;
    if (b <= a || seg <= 1e-9) continue;
    const ds = (centre.s[i]! - centre.s[i - 1]!) / seg;
    const dy = (centre.y[i]! - centre.y[i - 1]!) / seg;
    seen += (b - a) * Math.abs(ce * dy - se * ds);
    span += b - a;
  }
  return {
    L,
    foldU,
    band,
    chaseView: span > 1e-6 ? seen / span : ce,
    drape: (u, v) => {
      const p = col(v);
      const [s, y] = along(p, u * L);
      // M154 polish r2: soft FOLDS, so the resting cloth reads as cloth and
      // not a flat board (the monster truck's): ridges running down the
      // cloth, growing from nothing at the rail to CAPE_FOLD at the hem,
      // lifted OFF the body along the path's outward normal (up where it
      // lies, back where it hangs), never into it.
      const f = capeFold(u, v, W) * hemTaper(u);
      if (f <= 0) return [s - u * L, gather(u, v), anchor.y - y];
      const [s2, y2] = along(p, Math.min(L, u * L + 0.03));
      const [s1, y1] = along(p, Math.max(0, u * L - 0.03));
      const ts = s2 - s1;
      const ty = y2 - y1;
      const tl = Math.hypot(ts, ty) || 1;
      const ns = -ty / tl;
      const ny = ts / tl;
      return [s + ns * f - u * L, gather(u, v), anchor.y - (y + ny * f)];
    },
    fly: (u, v) => {
      const j = Math.max(0, Math.min(COLS, Math.round(v * COLS)));
      return [0, gather(u, v), anchor.y - flyY(j, u * L)];
    },
    restAt: (x, z) => {
      const v = 0.5 - (x - anchor.x) / W;
      if (v < 0 || v > 1) return null;
      const p = col(v);
      const s = anchor.z - z;
      if (s < 0) return null;
      for (let i = 1; i < p.s.length; i++) {
        if (p.len[i]! > L) return null;
        if (p.s[i]! >= s && p.s[i]! > p.s[i - 1]!) {
          const f = (s - p.s[i - 1]!) / (p.s[i]! - p.s[i - 1]!);
          return p.y[i - 1]! + (p.y[i]! - p.y[i - 1]!) * f;
        }
      }
      return null;
    },
    flyAt: (x, z) => {
      const v = 0.5 - (x - anchor.x) / W;
      const s = anchor.z - z;
      if (v < 0 || v > 1 || s < 0 || s > L) return null;
      return flyY(Math.max(0, Math.min(COLS, Math.round(v * COLS))), s);
    },
  };
}
